import React, { useEffect, useState } from "react"
import { View, StyleSheet, Text, TouchableOpacity } from "react-native"
import { ModalSkeleton } from "@common/modals/ModalSkeleton"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"

/**
 * Option discrète (power user) du reveal du programme : répartir le pack d'heures
 * hebdo entre les matières. Ouverte en tapant une chip de la légende matière du
 * calendrier. Steppers +/− par matière, somme VERROUILLÉE au pack d'heures (on
 * prend / rend l'heure à une matière voisine), donc le total ne dérive jamais.
 *
 * Bornes par matière : plancher 1 h (aucune matière ne descend sous 1 h) ;
 * plafond = pack d'heures − (nb matières − 1) — une matière peut prendre toutes
 * les heures sauf l'heure plancher que CHAQUE autre matière conserve. Les +/−
 * sont désactivés à ces bornes (pour la matière déplacée ET la matière voisine).
 *
 * Au save → `onSave({ [subjectId]: hours })` ; le reveal écrit `subjectHours` dans
 * le contexte funnel et re-fetch le programme (le backend `program()` folde
 * `subject_hours` dans la profile_key et l'honore quand cohérent).
 *
 * Conventions projet : ModalSkeleton (ModalM), composants « M », DimensionsContext,
 * labels accessibles.
 */

// Plancher d'heures par matière (aucune matière sous 1 h).
const MIN_HOURS_PER_SUBJECT = 1

// Répartition par défaut (miroir de dispatchHours backend) : base égale avec un
// plancher de 1 h/matière, le reste aux premières matières (ordre = légende = rank).
// Quand le pack ne couvre pas 1 h/matière (total < nb matières), on retombe sur le
// split brut (le backend cappe ensuite) — l'UI ne crée jamais ce cas car le
// configurateur garantit créneaux ≥ matières.
const evenSplit = (subjectIds, total, step = 1) => {
    if (step > 1) {
        // En unités de `step` (un créneau = une matière), puis en heures.
        const units = evenSplit(subjectIds, Math.floor(total / step), 1)
        return Object.fromEntries(
            Object.entries(units).map(([id, u]) => [id, u * step])
        )
    }
    const n = subjectIds.length
    if (n === 0) return {}
    if (total < n) {
        const base = Math.floor(total / n)
        const remainder = total % n
        const result = {}
        subjectIds.forEach((id, i) => {
            result[id] = base + (i < remainder ? 1 : 0)
        })
        return result
    }
    // 1 h plancher à chacun, puis on distribue le surplus aux premières matières.
    const surplus = total - n
    const base = Math.floor(surplus / n)
    const remainder = surplus % n
    const result = {}
    subjectIds.forEach((id, i) => {
        result[id] = MIN_HOURS_PER_SUBJECT + base + (i < remainder ? 1 : 0)
    })
    return result
}

const SummerSubjectHoursModal = ({
    isVisible,
    onClose,
    subjects, // [{ id, name }] in legend (rank) order
    weeklyHours, // total heures à répartir (verrouillé)
    // Le pack n'est hebdomadaire que côté été : un stage vendu au créneau
    // répartit les heures d'UN week-end. Le libellé se dit, il ne se devine pas.
    periodLabel = "/semaine",
    initialAllocation, // { [subjectId]: hours } | null/empty → split auto
    onSave,
    // Le pas de la répartition. 1 h côté été ; un créneau (2 h) pour un stage
    // vendu au créneau — une séance porte UNE matière, on ne coupe pas un
    // créneau en deux.
    step = 1,
}) => {
    const minPerSubject = MIN_HOURS_PER_SUBJECT * step
    const subjectIds = (subjects || []).map((s) => String(s.id))

    // Plafond par matière : une matière peut prendre tout le pack SAUF l'heure
    // plancher que chaque AUTRE matière conserve → pack − (nb matières − 1).
    // (Borne au plancher quand il n'y a qu'une matière / pack trop petit.)
    const maxPerSubject = Math.max(
        minPerSubject,
        (weeklyHours || 0) - Math.max(subjectIds.length - 1, 0) * step
    )

    const [allocation, setAllocation] = useState({})

    // (Re)sème la répartition à l'ouverture : l'allocation fournie si elle couvre
    // exactement les matières, somme au pack d'heures ET respecte les bornes par
    // matière (≥ plancher, ≤ plafond), sinon le split auto.
    useEffect(() => {
        if (!isVisible) return
        const covers =
            subjectIds.length > 0 &&
            initialAllocation &&
            subjectIds.every(
                (id) =>
                    initialAllocation[id] != null &&
                    initialAllocation[id] >= minPerSubject &&
                    initialAllocation[id] <= maxPerSubject
            ) &&
            Object.keys(initialAllocation).length === subjectIds.length
        const total = subjectIds.reduce(
            (sum, id) => sum + (initialAllocation?.[id] || 0),
            0
        )
        if (covers && total === weeklyHours) {
            setAllocation(
                subjectIds.reduce((acc, id) => {
                    acc[id] = initialAllocation[id]
                    return acc
                }, {})
            )
        } else {
            setAllocation(evenSplit(subjectIds, weeklyHours, step))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, weeklyHours, JSON.stringify(subjectIds)])

    // Stepper : +1 / −1 h sur une matière, en gardant la somme = pack d'heures (on
    // prend / rend l'heure à la matière voisine). Bornes par matière :
    // [MIN_HOURS_PER_SUBJECT, maxPerSubject] — respectées pour la matière déplacée
    // ET la matière voisine (plancher 1 h chacune).
    const adjust = (subjectId, delta) => {
        const current = allocation[subjectId] || 0
        const next = current + delta
        if (next < minPerSubject || next > maxPerSubject) return
        const others = subjectIds.filter((id) => id !== subjectId)
        if (others.length === 0) return
        let donorId = null
        if (delta > 0) {
            // On prend 1 h à une voisine qui peut descendre sans passer sous le plancher.
            donorId = others.find(
                (id) => (allocation[id] || 0) - step >= minPerSubject
            )
            if (!donorId) return // personne à qui prendre l'heure
        } else {
            // On rend 1 h à une voisine qui peut monter sans dépasser le plafond.
            donorId = others.find(
                (id) => (allocation[id] || 0) + step <= maxPerSubject
            )
            if (!donorId) return
        }
        setAllocation((prev) => ({
            ...prev,
            [subjectId]: next,
            [donorId]: (prev[donorId] || 0) - delta,
        }))
    }

    const total = subjectIds.reduce((sum, id) => sum + (allocation[id] || 0), 0)
    const nameById = {}
    ;(subjects || []).forEach((s) => {
        nameById[String(s.id)] = s.name
    })

    return (
        <ModalSkeleton
            isVisible={isVisible}
            onClose={onClose}
            title="Heures par matière"
        >
            <View style={{ width: "100%" }}>
                <Text style={styles.hint}>
                    Répartissez les {weeklyHours} h{periodLabel} entre les
                    matières (au moins{" "}
                    {step > 1 ? `un créneau de ${minPerSubject} h` : "1 h"} par
                    matière).
                </Text>

                {subjectIds.map((id) => {
                    const h = allocation[id] || 0
                    return (
                        <View key={id} style={styles.row}>
                            <Text style={styles.name} numberOfLines={1}>
                                {nameById[id] || "Matière"}
                            </Text>
                            <View style={styles.stepper}>
                                <TouchableOpacity
                                    activeOpacity={0.7}
                                    accessibilityRole="button"
                                    accessibilityLabel={`Retirer une heure à ${nameById[id] || "la matière"}`}
                                    onPress={() => adjust(id, -step)}
                                    disabled={h <= minPerSubject}
                                    style={[
                                        styles.stepBtn,
                                        h <= minPerSubject &&
                                            styles.stepBtnDisabled,
                                    ]}
                                >
                                    <Text style={styles.stepBtnText}>−</Text>
                                </TouchableOpacity>
                                <Text
                                    style={styles.stepValue}
                                    accessibilityLabel={`${h} heures`}
                                >
                                    {h} h
                                </Text>
                                <TouchableOpacity
                                    activeOpacity={0.7}
                                    accessibilityRole="button"
                                    accessibilityLabel={`Ajouter une heure à ${nameById[id] || "la matière"}`}
                                    onPress={() => adjust(id, step)}
                                    disabled={h >= maxPerSubject}
                                    style={[
                                        styles.stepBtn,
                                        h >= maxPerSubject &&
                                            styles.stepBtnDisabled,
                                    ]}
                                >
                                    <Text style={styles.stepBtnText}>+</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    )
                })}

                <Text style={styles.total}>
                    Total : {total} / {weeklyHours} h
                </Text>

                <FilledButtonM
                    label="Enregistrer"
                    onPress={() => onSave(allocation)}
                    style={{ width: "100%", marginTop: 16, marginBottom: 0 }}
                />
            </View>
        </ModalSkeleton>
    )
}

const styles = StyleSheet.create({
    hint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
        marginBottom: 14,
        lineHeight: 18,
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingVertical: 6,
    },
    name: {
        flex: 1,
        fontFamily: "Montserrat-Bold",
        fontSize: 14,
        color: theme.colors.dark || theme.colors.primary,
        marginRight: 10,
    },
    stepper: { flexDirection: "row", alignItems: "center", gap: 10 },
    stepBtn: {
        width: 32,
        height: 32,
        borderRadius: 16,
        borderWidth: 1.5,
        borderColor: theme.colors.primary,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.colors.primaryLighter,
    },
    stepBtnDisabled: {
        opacity: 0.35,
    },
    stepBtnText: {
        fontFamily: "Montserrat-Bold",
        fontSize: 16,
        color: theme.colors.primary,
        lineHeight: 18,
    },
    stepValue: {
        fontFamily: "Montserrat-Bold",
        fontSize: 14,
        color: theme.colors.primary,
        minWidth: 36,
        textAlign: "center",
    },
    total: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.grey,
        marginTop: 12,
        textAlign: "right",
    },
})

export default SummerSubjectHoursModal
